/**
 * Admin Command durable cache (localStorage-backed, dependency-free).
 *
 * The admin overview is expensive to rebuild: the Appwrite fetch is fast
 * (~1-2s) but the per-team N8N enrichment (credits + campaign metrics) runs
 * serially and takes minutes for the full client list. Teams and users rarely
 * change, so we persist BOTH layers to localStorage:
 *
 *   - overview (teams + users + prefs)  → hydrates the table instantly on load
 *   - per-team metrics                   → keyed by a team fingerprint
 *
 * Change detection: each team gets a fingerprint of the fields that could
 * change its metrics (team id, owner email, updated timestamp). A cached
 * metric entry is reused when the fingerprint matches AND it hasn't exceeded
 * its freshness window. Otherwise it's re-enriched in the background.
 *
 * Storage layout (all namespaced under "adminCache."):
 *   adminCache.overview.v1        → { savedAt, data }
 *   adminCache.metrics.v1.<id>    → { savedAt, fingerprint, metrics }
 *
 * All reads/writes are fail-safe: private-mode/blocked storage degrades to
 * no caching, never to a crash.
 */

const OVERVIEW_KEY = 'adminCache.overview.v1';
const METRICS_PREFIX = 'adminCache.metrics.v1.';
/** How long a cached per-team metric stays fresh (ms). 15 minutes. */
const METRICS_TTL_MS = 15 * 60 * 1000;

const safeGet = (key: string): string | null => {
  try { return localStorage.getItem(key); } catch { return null; }
};
const safeSet = (key: string, value: string): void => {
  try { localStorage.setItem(key, value); } catch { /* storage full/blocked — degrade silently */ }
};
const safeRemove = (key: string): void => {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
};

/** Fields that, when changed, invalidate a team's cached metrics. */
export const teamFingerprint = (team: { $id: string; owner_email?: string | null; $updatedAt?: string; total?: number }): string =>
  [team.$id, team.owner_email || '', team.$updatedAt || '', team.total ?? 0].join('|');

export interface CachedOverview<T> {
  savedAt: number;
  data: T;
}

export const adminCache = {
  /** Persist the overview payload. */
  saveOverview<T>(data: T): void {
    safeSet(OVERVIEW_KEY, JSON.stringify({ savedAt: Date.now(), data }));
  },

  /** Hydrate the cached overview. Returns null when absent/unreadable. */
  loadOverview<T>(): CachedOverview<T> | null {
    const raw = safeGet(OVERVIEW_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as CachedOverview<T>;
      return parsed?.data ? parsed : null;
    } catch { return null; }
  },

  /** Persist one team's metrics under its current fingerprint. */
  saveTeamMetrics(teamId: string, fingerprint: string, metrics: unknown): void {
    safeSet(METRICS_PREFIX + teamId, JSON.stringify({ savedAt: Date.now(), fingerprint, metrics }));
  },

  /**
   * Load a team's cached metrics. Fresh only when the fingerprint matches the
   * live team AND the entry is within its TTL.
   */
  loadTeamMetrics<T>(fingerprint: string): { metrics: T; savedAt: number } | null {
    const entry = readTeamMetrics<T>(fingerprint);
    if (!entry) return null;
    if (Date.now() - entry.savedAt > METRICS_TTL_MS) return null;
    return entry;
  },

  /**
   * Load a team's cached metrics matching the fingerprint, IGNORING the TTL —
   * last-known values. Used as a display baseline while credits re-enrich, so
   * a merely-expired entry doesn't blank out campaign counts that no longer
   * get their own n8n call.
   */
  loadTeamMetricsLastKnown<T>(fingerprint: string): { metrics: T; savedAt: number } | null {
    return readTeamMetrics<T>(fingerprint);
  },

  /** Drop every cached metric entry (e.g. overview replaced with a different team set). */
  clearTeamMetrics(): void {
    try {
      Object.keys(localStorage)
        .filter((key) => key.startsWith(METRICS_PREFIX))
        .forEach((key) => localStorage.removeItem(key));
    } catch { /* ignore */ }
  },

  /** Full reset — overview + metrics. */
  clearAll(): void {
    safeRemove(OVERVIEW_KEY);
    adminCache.clearTeamMetrics();
  },

  metricsTtlMs: METRICS_TTL_MS,
};

/** The fingerprint's first segment is the team id (see teamFingerprint). */
const teamId_fromFingerprint = (fingerprint: string): string => fingerprint.split('|')[0] || '';

/** Shared read: fingerprint-matched entry, no TTL check. */
const readTeamMetrics = <T>(fingerprint: string): { metrics: T; savedAt: number } | null => {
  const raw = safeGet(METRICS_PREFIX + teamId_fromFingerprint(fingerprint));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as { savedAt: number; fingerprint: string; metrics: T };
    if (!parsed?.metrics || parsed.fingerprint !== fingerprint) return null;
    return { metrics: parsed.metrics, savedAt: parsed.savedAt };
  } catch { return null; }
};
